import { compile, parse } from "path-to-regexp";
import { logger } from "@origin-digital/reporting-client";
import { NavToTopics } from "@origin-digital/event-dispatcher";
import { CustomerType, NavFlow, Tab } from "@origin-digital/platform-enums";
import { concessions } from "./mcApps/concessions";
import { profile } from "./mcApps/profile";
import { paymentMethods } from "./mcApps/paymentMethods";
import { paymentPlan } from "./mcApps/paymentPlan";
import * as mcApps from "./mcApps";

import {
  FALLBACK_INTENT,
  FALLBACK_MCAPP_NAME,
  IMcAppType,
  McAppListMap,
  McAppName,
  Params,
  PlatformParameters,
  Primitives,
} from "./mcApp.types";
import { selfMeterRead } from "./mcApps/selfMeterRead";
import { refunds } from "./mcApps/refunds";
import { findMcAppByPath } from "./intents";
import { getIntent } from "./mcApp";

export const mcAppList: IMcAppType[] = [
  ...Object.values(mcApps),
  ...concessions,
  ...paymentMethods,
  ...paymentPlan,
  ...profile,
  ...refunds,
  ...selfMeterRead,
];

const getMcAppMap = (appsList: IMcAppType[]): Record<McAppName, IMcAppType> =>
  Object.values(appsList).reduce((obj, mcApp) => {
    if (!mcApp || !mcApp.name) {
      logger.error(`[@od/mcapp-reg] mcApp not defined`, mcApp);
      return obj;
    }
    if (obj[mcApp.name]) {
      logger.error(`[@od/mcapp-reg] name already exits: ${mcApp.name}`, [
        mcApp,
        obj[mcApp.name],
      ]);
    }
    obj[mcApp.name] = mcApp;
    return obj;
  }, {} as McAppListMap);

export const mcAppMap: McAppListMap = getMcAppMap(mcAppList);

// export for testing
export const intentMap = Object.entries(mcAppMap).reduce(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  (obj, [name, mcApp]) => {
    let intent: string | null = null;
    if (mcApp.conditions) {
      if (mcApp.conditions.intent) {
        intent = mcApp.conditions.intent;
      } else {
        logger.error(
          `[@od/mcapp-reg] intent missing from conditions: ${mcApp.name}`,
          mcApp
        );
      }
    }
    if (intent == null && obj[mcApp.name]) {
      logger.error(
        `[@od/mcapp-reg] intent named already in intentMap, needs conditions: ${mcApp.name}`,
        mcApp
      );
    }
    if (intent == null) {
      intent = mcApp.name;
    }
    if (obj[intent] == null) {
      obj[intent] = [];
    }
    obj[intent].push(mcApp.name);
    return obj;
  },
  {} as Record<string, McAppName[]>
);

/**
 * creates a uri encoded query string from key value pairs
 *
 * @param pairs - key value pair
 * @param append - boolean if we are appending "&" or not "?"
 */
export const queryString = (
  pairs: Record<string, Primitives | Primitives[]>,
  append: boolean = false
): string => {
  const q = Object.keys(pairs).reduce<string>((str: string, key: string) => {
    const value = pairs[key];
    if (value == null) return str;
    const param = `${str.length > 0 ? "&" : ""}${encodeURIComponent(
      key
    )}=${encodeURIComponent(Array.isArray(value) ? value.join(",") : value)}`;
    return `${str}${param}`;
  }, "");

  return q.length === 0 ? "" : append ? `&${q}` : `?${q}`;
};

export const findConditionalMcAppName = (
  intent: string,
  platform: PlatformParameters,
  parameters: Params
): McAppName | undefined => {
  const intents = intentMap[intent];
  if (!intents) {
    throw new Error(
      `[@od/mcapp-reg] cannot find intent: '${intent}' in intentMap`
    );
  }
  const mcAppName = intents.find((myName): boolean => {
    const myApp: IMcAppType = mcAppMap[myName];
    const { conditions } = myApp;
    // if there are no conditions then it passed
    if (!conditions) return true;

    // for the platform to be satisfied the conditions must either be:
    //  - not set, or
    //  - match exactly to the platform
    const platformSatisfied = Object.entries(platform).every(
      ([key, value]) => conditions[key] == null || conditions[key] === value
    );
    let parametersSatisfied = true;
    if (conditions.parameters) {
      // for the parameters to be satisfied the conditions of the mc app must either be:
      //  - if null / undefined, the parameters then must be the same or false
      //  - if defined, the parameters then must then match exactly
      parametersSatisfied = Object.entries(conditions.parameters || {}).every(
        // uses double == for null or undefined, false also is the same as null or undefined
        ([key, value]) =>
          value == null
            ? parameters[key] == null ||
              parameters[key] == false ||
              String(parameters[key]).toLowerCase() == "false"
            : parameters[key] === value ||
              String(parameters[key]).toLowerCase() ===
                String(value).toLowerCase()
      );
    }
    return parametersSatisfied && platformSatisfied;
  });
  return mcAppName;
};

const getRoute = (mcApp: IMcAppType, parameters: Params) => {
  const { path } = mcApp;

  const queryParam: Record<string, Primitives | Primitives[]> = {};
  const pathParam: Record<string, Primitives | Primitives[]> = {};

  // get the token from path, /help/:category return [{name: category, ...}]
  const tokens = parse(path);

  // orginises parameters into query string and path tokens
  Object.keys(parameters).forEach((key) => {
    const hasToken = tokens.some(
      (t) => typeof t === "object" && t.name === key
    );
    if (hasToken) {
      pathParam[key] = parameters[key];
    } else {
      queryParam[key] = parameters[key];
    }
  });
  const toPath = compile(path);
  const pathWithTokens = toPath(pathParam);

  // TODO throw error if require parameter is missing form queryParam

  const query = queryString(queryParam, pathWithTokens.indexOf("?") === 0);
  return `${pathWithTokens}${query}`;
};

/**
 * the fallback is computed on the users intent
 * @param string
 */
export const getFallbackFromIntent = (
  intent: string,
  platform: PlatformParameters
): string => {
  // when we suppport /navto endpoint we can use this logic

  // const intents = intentMap[intent];
  // if (intents) {
  //   const isAuthedIntent = intents.some((mcAppName) =>
  //     isAuthenticated(mcAppMap[mcAppName])
  //   );
  //   if (isAuthedIntent) {
  //     // fallback to /auth/callback?redirect=/navto/${intent}
  //   }
  // }
  const fallbackMcAppName = findConditionalMcAppName(
    FALLBACK_INTENT,
    platform,
    {}
  );
  return fallbackMcAppName ?? FALLBACK_MCAPP_NAME;
};

export type IIntentRoute = Pick<IMcAppType, "navFlow" | "parent" | "title"> & {
  url: string;
};

export const getIntentRoute = (
  intent: string,
  platform: PlatformParameters,
  parameters: Params = {}
): IIntentRoute => {
  let mcAppName = findConditionalMcAppName(intent, platform, parameters);
  if (!mcAppName) {
    logger.error(
      `[@od/mcapp-reg] cannot find intent: '${intent}', conditions`,
      {
        parameters,
        platform,
        intent,
      }
    );
    mcAppName = getFallbackFromIntent(intent, platform);
  }
  const mcApp = mcAppMap[mcAppName];
  try {
    const url = getRoute(mcApp, parameters);
    const { navFlow, parent, title } = mcApp;
    return { url, navFlow, parent, title };
  } catch (error) {
    logger.error(`[@od/mcapp-reg] cannot create route for: `, {
      intent,
      mcApp,
      parameters,
      error,
    });
    const fallbackMcAppName =
      findConditionalMcAppName(FALLBACK_INTENT, platform, parameters) ||
      FALLBACK_MCAPP_NAME;
    const { navFlow, parent, title, path } = mcAppMap[fallbackMcAppName];
    return { navFlow, parent, title, url: path };
  }
};

export interface IAppRoute {
  url: string;
  navFlow: NavFlow;
  parent: Tab;
  title: string;
}
/**
 * translates old contract to new contract
 * @param platform
 * @param payload
 */
export const getAppRoute = (
  { channel, customerType, scopedToken }: PlatformParameters,
  payload: NavToTopics["payload"]
): IAppRoute => {
  const { to: intentName, parameters = {} } = payload;
  // remove parameters that will not form the query string
  // these will be provided by the platform in the future
  const {
    isUnauthenticated,
    customerType: paramCustomerType,
    ...rest
  } = parameters as Record<string, any>;

  // allows the customer type to be overridden
  if (isUnauthenticated) {
    customerType = CustomerType.unauthenticated;
  } else if (paramCustomerType === CustomerType.kraken) {
    customerType = CustomerType.kraken;
  } else if (paramCustomerType === CustomerType.sap) {
    customerType = CustomerType.sap;
  }

  const platform: PlatformParameters = {
    channel, // translates types
    customerType,
    scopedToken,
  };
  return getIntentRoute(intentName, platform, rest) as IAppRoute;
};

/**
 * Given a path, ensure the customer is on the correct mc-app.
 *
 * If the path is for an unknown mc-app, ignore
 * If the mc-app matches, done
 *
 *
 * Fallback rules
 *   - if origin path is auth
 *
 *
 * @param path
 * @param platform
 * @param parameters
 */
export const verifyIntentPath = (
  path: string,
  platform: PlatformParameters,
  parameters: Record<string, any> = {},
  routes?: IMcAppType[]
): string | true | undefined => {
  const mcAppByPath = findMcAppByPath(routes ?? mcAppList, path);
  if (!mcAppByPath) {
    logger.warn(`[@od/mcapp-reg] cannot find mcAppByPath for ${path}`);
    return undefined; // cannot find the intent, nothing we can do
  }
  const intent = getIntent(mcAppByPath);
  const mcAppName = findConditionalMcAppName(intent, platform, parameters);
  if (mcAppName) {
    if (mcAppByPath.name === mcAppName) {
      return true; // we are on the correct path
    } else {
      return intent; // another mc-app satisfies the conditions
    }
  } else {
    // fallback, mc-app cannot be found
    return FALLBACK_INTENT;
  }
  return undefined;
};
